import { createSignal, onMount, Show, For } from 'solid-js';
import { A, useLocation } from '@solidjs/router';
import { useApi, isOk } from '../api';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import BotStatusBadge, { type BotState } from './BotStatusBadge';
import { BOT_ACTIONS, botAccessLabelKey, can } from '../services/resourceActions';
import styles from './BotManageNav.module.css';

interface BotSummary {
    id: string;
    minecraftUuid: string;
    minecraftUsername: string;
    isOwner: boolean;
    myActions: string[];
    state: BotState;
}

export default function BotManageNav(props: { botId: string }) {
    const api = useApi();
    const { t } = useTranslation();
    const location = useLocation();

    const [bot, setBot] = createSignal<BotSummary | null>(null);
    const [loading, setLoading] = createSignal(true);

    onMount(async () => {
        const res = await api.botRegistry.getBot(props.botId);
        if (isOk(res.data)) setBot(res.data.value as unknown as BotSummary);
        setLoading(false);
    });

    const mine = () => bot()?.myActions;

    const tabs = () => [
        { href: `/bots/${props.botId}/manage`, icon: 'fa-gauge', label: 'BOT_MANAGE_TAB_OVERVIEW', exact: true },
        ...(can(mine(), BOT_ACTIONS.AccessRead) ? [{ href: `/bots/${props.botId}/manage/access`, icon: 'fa-users-gear', label: 'BOT_MANAGE_TAB_ACCESS', exact: false }] : []),
        ...(can(mine(), BOT_ACTIONS.CredentialsManage) ? [{ href: `/bots/${props.botId}/profile`, icon: 'fa-user-pen', label: 'BOT_PROFILE_TITLE', exact: false }] : []),
        ...(can(mine(), BOT_ACTIONS.AuditRead) ? [{ href: `/bots/${props.botId}/auditlog`, icon: 'fa-clipboard-list', label: 'BOT_AUDIT_LOG', exact: false }] : []),
    ];

    const isActive = (href: string, exact: boolean) =>
        exact ? location.pathname === href : location.pathname.startsWith(href);

    return (
        <div class={styles.nav}>
            <div class={styles.identityRow}>
                <A href="/bots" class={styles.backLink} title={t('BOT_MANAGE_BACK')}>
                    <i class="fa-solid fa-arrow-left" />
                </A>
                <Show when={!loading()} fallback={<div class={styles.avatarSkeleton} />}>
                    <img
                        src={`https://mc-heads.net/avatar/${bot()?.minecraftUuid}/48`}
                        alt={bot()?.minecraftUsername}
                        class={styles.avatar}
                    />
                </Show>
                <div class={styles.identityText}>
                    <Show when={!loading()} fallback={<div class={styles.textSkeleton} />}>
                        <div class={styles.username}>{bot()?.minecraftUsername}</div>
                        <div class={styles.uuid}>{bot()?.minecraftUuid}</div>
                    </Show>
                </div>
                <Show when={!loading() && bot()}>
                    <BotStatusBadge state={bot()!.state} />
                    <span class={styles.levelPill}>
                        <Text val={botAccessLabelKey(bot()!.myActions)} />
                    </span>
                </Show>
            </div>

            <div class={styles.tabs}>
                <For each={tabs()}>
                    {(tab) => (
                        <A
                            href={tab.href}
                            class={`${styles.tab} ${isActive(tab.href, tab.exact) ? styles.tabActive : ''}`}
                        >
                            <i class={`fa-solid ${tab.icon}`} />
                            <Text val={tab.label} />
                        </A>
                    )}
                </For>
            </div>
        </div>
    );
}
